import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import axios from "@dreamchimney/react-axios";
import { getSleevesByTag } from "~/api/getSleeves";
import SleeveMini from "~/Components/SleeveMini";

const TAG_QUERY_LIMIT = 4;
const RESULT_LIMIT = 4;

const normalizeTags = (tags) => {
  const list = Array.isArray(tags) ? tags : String(tags || "").split(",");
  return list.map((tag) => String(tag || "").trim()).filter(Boolean);
};

const getSleeveTagList = (sleeve) => {
  if (sleeve.tags) return normalizeTags(sleeve.tags);
  return normalizeTags(sleeve.notes);
};

const SimilarSleeves = ({ sleeveId, tags = [] }) => {
  const [sleeves, setSleeves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const sourceTags = normalizeTags(tags).slice(0, TAG_QUERY_LIMIT);

    if (!sleeveId || sourceTags.length === 0) {
      setSleeves([]);
      setLoading(false);
      return undefined;
    }

    const load = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all(
          sourceTags.map((tag) =>
            getSleevesByTag(axios, tag, 1).catch((e) => {
              console.log("failure:", e);
              return { data: { sleeves: [] } };
            })
          )
        );

        if (cancelled) return;

        const sourceSet = new Set(sourceTags.map((tag) => tag.toLowerCase()));
        const byId = {};

        responses.forEach((response) => {
          const list = (response.data && response.data.sleeves) || [];
          list.forEach((sleeve) => {
            if (!sleeve || !sleeve.id || String(sleeve.id) === String(sleeveId)) return;

            const overlap = getSleeveTagList(sleeve).filter((tag) => sourceSet.has(tag.toLowerCase())).length;
            if (overlap === 0) return;

            const existing = byId[sleeve.id];
            if (!existing || overlap > existing.score) {
              byId[sleeve.id] = { sleeve, score: overlap };
            }
          });
        });

        const ranked = Object.keys(byId)
          .map((id) => byId[id])
          .sort((a, b) => b.score - a.score || b.sleeve.id - a.sleeve.id)
          .slice(0, RESULT_LIMIT)
          .map((entry) => entry.sleeve);

        setSleeves(ranked);
      } catch (e) {
        console.log("failure:", e);
        if (!cancelled) setSleeves([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sleeveId, normalizeTags(tags).slice(0, TAG_QUERY_LIMIT).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loading && sleeves.length === 0) return null;

  return (
    <div className="similar-sleeves">
      <h4 className="sleeve-section-title">Similar Sleeves</h4>
      {loading ? (
        <Skeleton count={2} />
      ) : (
        <ul className="results cover-wall similar-sleeves-list">
          {sleeves.map((sleeve) => (
            <li key={sleeve.id}>
              <SleeveMini sleeve={sleeve} showCaption />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SimilarSleeves;
